import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronRight, ChevronUp, Clapperboard, FileSpreadsheet, FileText, Link2, Presentation, Receipt, Sprout, Table2 } from 'lucide-react';

import AccountsStatsPage, { type ReportKind } from '../accounts-stats/AccountsStatsPage';
import { StatsSubScreen } from './StatsSubScreen';
import { Button } from '../../components/ui/button';
import { cn } from '../../components/ui/utils';
import { readRemembered, writeRemembered } from '../../shared/lib/remembered';

const REPORTS: {
  id: ReportKind;
  title: string;
  format: string;
  description: string;
  icon: typeof FileSpreadsheet;
}[] = [
  {
    id: 'xlsx',
    title: 'Таблица с метриками',
    format: 'Excel · 1 файл',
    description: 'Аккаунт за период: каждая публикация строкой — ссылка, дата, язык, тема, просмотры и скрин',
    icon: FileSpreadsheet,
  },
  {
    id: 'docx',
    title: 'Сводка для отчётности',
    format: 'Google Docs или Word · 1 файл',
    description:
      'Бывший «Отчёт министру»: итоги и публикации в готовой формулировке — по аккаунту за период или по списку ссылок',
    icon: FileText,
  },
  {
    id: 'links',
    title: 'Ссылки и темы',
    format: 'Google Docs или Word · 2 файла',
    description: 'Аккаунт за период: перечень публикаций с темами — один файл со скринами, второй без',
    icon: Link2,
  },
  {
    id: 'top',
    title: 'Топ видео',
    format: 'Google Docs или Word · 1 файл',
    description: 'Несколько аккаунтов за период: самые просматриваемые ролики с топовыми комментариями',
    icon: Clapperboard,
  },
  {
    id: 'seeding',
    title: 'Отработано',
    format: 'Google Docs или Word · 1 файл',
    description:
      'Еженедельный по ссылкам: копии ролика в разных пабликах складываются в один посев, в файле — топ посевов',
    icon: Sprout,
  },
  {
    id: 'presentation',
    title: 'Презентация',
    format: 'PDF, PowerPoint или SVG · 1 файл',
    description: 'Клиенту по ссылкам: титул, слайд на публикацию со скрином и цифрами, итог',
    icon: Presentation,
  },
  {
    id: 'seeding-sheet',
    title: 'Посев в таблице',
    format: 'Excel · 1 файл',
    description:
      'Клиенту по ссылкам, по его образцу: строка на каждое размещение, скрин статистики в ячейке, без группировки и итогов',
    icon: Table2,
  },
  {
    id: 'act',
    title: 'Отчёт об услугах',
    format: 'Google Docs или Word · 1 файл',
    description:
      'Бухгалтерии клиента по ссылкам: таблица размещений со стоимостью и экранами статистики',
    icon: Receipt,
  },
];

const KINDS = REPORTS.map((r) => r.id);

function isReportKind(value: string | null): value is ReportKind {
  return !!value && (KINDS as string[]).includes(value);
}

const REMEMBERED_REPORT = 'report:kind';

/**
 * Выбор выгрузки карточками, а не вкладками: отчётов уже восемь, и будут
 * добавляться новые — вкладки на такое не тянутся, а карточка ещё и место
 * под объяснение, что именно окажется в файле.
 *
 * Выбранный отчёт помнится: на следующем заходе карточки свёрнуты в одну
 * строку «Отчёт: … · Сменить», и форма сразу на первом экране.
 */
export function StatsReportPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const reportParam = params.get('report');
  const remembered = readRemembered<string | null>(REMEMBERED_REPORT, null);
  const report: ReportKind | null = isReportKind(reportParam)
    ? reportParam
    : isReportKind(remembered)
      ? remembered
      : null;
  // Без выбранного отчёта (первый заход) список открыт, иначе свёрнут.
  const [pickerOpen, setPickerOpen] = useState(report === null);
  const formRef = useRef<HTMLDivElement>(null);
  const [scrollToForm, setScrollToForm] = useState(false);

  useEffect(() => {
    if (isReportKind(reportParam)) writeRemembered(REMEMBERED_REPORT, reportParam);
  }, [reportParam]);

  useEffect(() => {
    if (!scrollToForm) return;
    setScrollToForm(false);
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [scrollToForm]);

  const select = (next: ReportKind) => {
    writeRemembered(REMEMBERED_REPORT, next);
    setParams(
      (prev) => {
        const updated = new URLSearchParams(prev);
        updated.set('report', next);
        return updated;
      },
      { replace: true },
    );
    setPickerOpen(false);
    setScrollToForm(true);
  };

  const active = REPORTS.find((r) => r.id === report);

  return (
    <StatsSubScreen title="Выгрузки" description="Отчёты по аккаунтам и ссылкам — Excel, Word, Google Docs и PDF">
      {!pickerOpen && active ? (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-card px-4 py-3">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <active.icon className="size-4" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground">Отчёт:</span>
              <span className="text-sm font-semibold">{active.title}</span>
              <span className="rounded-md bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                {active.format}
              </span>
            </span>
            <span className="mt-0.5 block text-xs text-muted-foreground">{active.description}</span>
          </span>
          <Button variant="outline" size="sm" onClick={() => setPickerOpen(true)}>
            Сменить
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {active && (
            <Button variant="ghost" size="sm" className="w-fit gap-1.5" onClick={() => setPickerOpen(false)}>
              <ChevronUp className="size-4" />
              Свернуть список
            </Button>
          )}
          {REPORTS.map(({ id, title, format, description, icon: Icon }) => {
            const isActive = report === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => select(id)}
                aria-pressed={isActive}
                className={cn(
                  'flex items-center gap-3.5 rounded-lg border p-4 text-left transition-colors',
                  'focus-visible:ring-ring/50 focus-visible:outline-none focus-visible:ring-[3px]',
                  isActive ? 'border-primary bg-primary/5' : 'bg-card hover:bg-accent',
                )}
              >
                <span
                  className={cn(
                    'flex size-10 shrink-0 items-center justify-center rounded-[10px]',
                    isActive ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
                  )}
                >
                  <Icon className="size-[19px]" />
                </span>

                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold">{title}</span>
                    <span className="rounded-md bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                      {format}
                    </span>
                  </span>
                  <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
                    {description}
                  </span>
                </span>

                <ChevronRight className="size-[17px] shrink-0 text-muted-foreground" />
              </button>
            );
          })}
        </div>
      )}

      <div ref={formRef} className="scroll-mt-4">
        {report && <AccountsStatsPage report={report} />}
      </div>
    </StatsSubScreen>
  );
}
